<div class="register-print-editor" *ngIf="template">
  <div class="editor-header">
    <div class="editor-title">
      <h1>{{isNew ? 'New Register Print Template' : template.name}}</h1>
      <span class="key-badge" *ngIf="template.key">{{template.key}}</span>
      <span class="default-badge" *ngIf="overridesBuiltIn">Overrides built-in</span>
      <span class="inactive-badge" *ngIf="template.active === false">Inactive</span>
    </div>
    <div class="editor-actions">
      <button class="button red" *ngIf="!isNew && template.active !== false" (click)="deactivate()">Deactivate</button>
      <button class="button" [disabled]="saving" (click)="save()">{{isNew ? 'Create Template' : 'Save Template'}}</button>
      <button class="button" (click)="close()">Close</button>
    </div>
  </div>

  <div class="editor-body">

    <div class="column general">
      <h3 class="section-title">General</h3>
      <div class="form-grid">
        <label class="wide" *ngIf="isNew">
          Start from
          <select [(ngModel)]="startFromKey" (ngModelChange)="onStartFromChanged()">
            <option *ngFor="let builtIn of builtIns" [value]="builtIn.key">{{builtIn.name}} ({{builtIn.key}})</option>
          </select>
        </label>
        <label class="wide">
          Name
          <input type="text" placeholder="template name" [(ngModel)]="template.name" />
        </label>
        <label class="wide">
          Key
          <input type="text" placeholder="auto-generated when blank" [(ngModel)]="template.key" />
        </label>
        <label class="wide">
          Description
          <input type="text" placeholder="what this printout is for" [(ngModel)]="template.description" />
        </label>
      </div>
      <p class="hint">Use the key of a built-in (<code>z-tape</code>, <code>x-tape</code>) to replace that printout on every register.</p>

      <h3 class="section-title">Data</h3>
      <div class="form-grid">
        <label class="wide">
          Source
          <select [(ngModel)]="template.source.key" (ngModelChange)="onSourceChanged()">
            <option *ngFor="let source of sources" [value]="source.key">{{source.name}}</option>
          </select>
        </label>
        <label class="wide" *ngIf="template.source.key === 'report'">
          Report
          <select [(ngModel)]="template.source.reportKey" (ngModelChange)="templateChanged()">
            <option value="">— choose a report —</option>
            <option *ngFor="let report of reports" [value]="report.key">{{report.name}}<ng-container *ngIf="report.category"> · {{report.category}}</ng-container></option>
          </select>
        </label>
        <label class="wide" *ngIf="sourceUsesScope">
          Scope
          <select [(ngModel)]="template.scope" (ngModelChange)="templateChanged()">
            <option value="">— source default ({{scopeLabel(source?.defaultScope || 'businessDay')}}) —</option>
            <option *ngFor="let scope of scopes" [value]="scope">{{scopeLabel(scope)}}</option>
          </select>
        </label>
      </div>
      <p class="hint" *ngIf="source?.description">{{source.description}}</p>
      <p class="hint" *ngIf="source?.confirm">The register asks for confirmation before this source runs.</p>
      <p class="hint" *ngIf="sourceUsesScope">A scope set on the menu action overrides the template's; one sent by the register overrides both.</p>

      <h3 class="section-title">Printer</h3>
      <div class="flag-row">
        <div class="flag">
          <app-toggle [checked]="template.logo !== false" (valueChanged)="template.logo = $event"></app-toggle>
          <span>Print the location logo</span>
        </div>
        <div class="flag">
          <app-toggle [checked]="template.cutReceipt !== false" (valueChanged)="template.cutReceipt = $event"></app-toggle>
          <span>Cut the paper</span>
        </div>
        <div class="flag">
          <app-toggle [checked]="template.openCashDrawer === true" (valueChanged)="template.openCashDrawer = $event"></app-toggle>
          <span>Open the cash drawer</span>
        </div>
      </div>
      <div class="form-grid">
        <label class="wide">
          Success message
          <input type="text" placeholder="{{template.name || 'Printout'}} sent to the printer." [(ngModel)]="template.successMessage" />
        </label>
      </div>

      <h3 class="section-title">Register menu</h3>
      <p class="hint" *ngIf="isNew">Create the template first, then choose which register menu actions print it.</p>
      <p class="hint" *ngIf="!isNew && menuActions.length === 0">No register menu actions can carry a printout.</p>
      <div class="row-list" *ngIf="!isNew">
        <div class="row-item action" *ngFor="let action of menuActions">
          <input type="checkbox" [checked]="printsThisTemplate(action)" [disabled]="binding[action._id]"
                 (change)="toggleAction(action, $event.target.checked)" />
          <div class="action-name">{{action.name}}<span class="action-group" *ngIf="action.groupName"> · {{action.groupName}}</span></div>
          <div class="action-template" *ngIf="!printsThisTemplate(action)">{{action.printKey || 'built-in'}}</div>
          <select class="action-scope" *ngIf="printsThisTemplate(action) && sourceUsesScope" [ngModel]="action.printScope || ''"
                  (ngModelChange)="changeActionScope(action, $event)" [disabled]="binding[action._id]">
            <option value="">template scope</option>
            <option *ngFor="let scope of scopes" [value]="scope">{{scopeLabel(scope)}}</option>
          </select>
        </div>
      </div>

      <div class="section-header">
        <h3>Reference</h3>
        <button class="button small" (click)="showReference = !showReference">{{showReference ? 'Hide' : 'Show'}}</button>
      </div>
      <div class="reference" *ngIf="showReference">
        <p class="hint">
          <code>&lt;%- value %&gt;</code> prints a value. <code>&lt;% code -%&gt;</code> runs JavaScript; the trailing
          <code>-%&gt;</code> swallows the line break so logic lines leave no blank lines. <code>&lt;%# comment -%&gt;</code>.
        </p>
        <h4>Data</h4>
        <div class="ref-row" *ngFor="let item of (referenceBySource[template.source.key === 'xTape' ? 'zTape' : template.source.key] || [])">
          <code>{{item.path}}</code>
          <span>{{item.note}}</span>
        </div>
        <div class="ref-row" *ngFor="let item of referenceCommon">
          <code>{{item.path}}</code>
          <span>{{item.note}}</span>
        </div>
        <h4>Helpers</h4>
        <div class="ref-row" *ngFor="let item of referenceHelpers">
          <code>{{item.sig}}</code>
          <span>{{item.note}}</span>
        </div>
      </div>
    </div>

    <div class="column source">
      <div class="section-header">
        <h3>Template</h3>
        <button class="button small" (click)="resetToBuiltIn()" [disabled]="!overridesBuiltIn && !startFromKey">Reset to built-in</button>
      </div>
      <textarea class="ejs-editor" spellcheck="false" autocapitalize="off" autocomplete="off"
                [(ngModel)]="template.ejs" (ngModelChange)="templateChanged()" (keydown)="onEditorKeydown($event)"></textarea>
    </div>

    <div class="column preview">
      <div class="section-header">
        <h3>Preview</h3>
        <span class="hint" *ngIf="previewLoading">rendering…</span>
      </div>
      <div class="preview-controls">
        <label>
          Location
          <select [(ngModel)]="previewLocationId" (ngModelChange)="onPreviewLocationChanged()">
            <option *ngFor="let location of registers" [value]="location._id">{{location.name}}</option>
          </select>
        </label>
        <label>
          Register
          <select [(ngModel)]="previewTerminalId" (ngModelChange)="renderPreview()">
            <option *ngFor="let terminal of previewTerminals" [value]="terminal._id">{{terminal.name}}</option>
          </select>
        </label>
        <label *ngIf="sourceUsesScope">
          Scope
          <select [(ngModel)]="previewScope" (ngModelChange)="renderPreview()">
            <option value="">as the template prints</option>
            <option *ngFor="let scope of scopes" [value]="scope">{{scopeLabel(scope)}}</option>
          </select>
        </label>
      </div>
      <p class="hint" *ngIf="previewScopeLabel">Window: {{previewScopeLabel}}</p>
      <div class="preview-error" *ngIf="previewError"><pre>{{previewError}}</pre></div>
      <div class="paper" *ngIf="!previewError">
        <div class="ruler">{{ruler}}</div>
        <pre class="receipt"><ng-container *ngFor="let line of previewLines"><span [class.too-long]="line.tooLong">{{line.text}}</span>
</ng-container></pre>
      </div>
      <p class="hint">Rendered by the server exactly as the register prints it, for the selected register right now. A Z-Tape previews without closing the period. Red lines are wider than the paper and will wrap.</p>
    </div>

  </div>
</div>
